import { useCallback, useEffect, useRef, useState } from 'react'
import { LegalFooter } from './components/LegalFooter'
import { LegalPage } from './components/LegalPage'
import { PullToRefresh } from './components/PullToRefresh'
import { PromoBanner } from './components/PromoBanner'
import { RoastCard } from './components/RoastCard'
import { RoastToast } from './components/RoastToast'
import { TabHistory } from './components/TabHistory'
import { TipCalculator } from './components/TipCalculator'
import { captureBillImage } from './lib/camera'
import { loadLastRoast, saveLastRoast } from './lib/lastRoast'
import { APP_NAME, APP_TAGLINE, PRIVACY_SECTIONS, TERMS_SECTIONS } from './lib/legal'
import {
  DEFAULT_TIP_PERCENT,
  centsToDollars,
  dollarsToCents,
  parseDollarInput,
  parsePercentInput,
  resolveAutoGratCents,
  tabPayTotalCents,
  tipCentsFromBill,
  tipPercentFromAmounts,
  todayISODate,
  type AutoGratEntryMode,
} from './lib/money'
import { recognizeReceipt } from './lib/ocr'
import { roastForHabits, roastForLiveBill, type RoastTone } from './lib/roasts'
import { tabSharePayloadFromSave, type TabSharePayload } from './lib/shareCard'
import { loadTabs, newTabId, saveTabs } from './lib/storage'
import type { Tab, VenueKind } from './lib/types'

type Screen = 'home' | 'privacy' | 'terms'

type TipEntryMode = 'preset' | 'percent' | 'amount'

export default function App() {
  const [tabs, setTabs] = useState<Tab[]>([])
  const [ready, setReady] = useState(false)
  const [screen, setScreen] = useState<Screen>('home')
  const [billInput, setBillInput] = useState('')
  const [presetPercent, setPresetPercent] = useState<number | null>(DEFAULT_TIP_PERCENT)
  const [tipEntryMode, setTipEntryMode] = useState<TipEntryMode>('preset')
  const [customPercentInput, setCustomPercentInput] = useState('')
  const [customTipInput, setCustomTipInput] = useState('')
  const [venue, setVenue] = useState('')
  const [kind, setKind] = useState<VenueKind>('bar')
  const [date, setDate] = useState(todayISODate)
  const [scanning, setScanning] = useState(false)
  const [openingCamera, setOpeningCamera] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<number[]>([])
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [source, setSource] = useState<Tab['source']>('manual')
  const [autoGratEnabled, setAutoGratEnabled] = useState(false)
  const [autoGratEntryMode, setAutoGratEntryMode] = useState<AutoGratEntryMode>('percent')
  const [autoGratPercentInput, setAutoGratPercentInput] = useState('')
  const [autoGratInput, setAutoGratInput] = useState('')
  const [savedRoast, setSavedRoast] = useState<string | null>(null)
  const [savedTone, setSavedTone] = useState<RoastTone | null>(null)
  const [lastRoast, setLastRoast] = useState<{ text: string; tone: RoastTone } | null>(null)
  const [toastKey, setToastKey] = useState(0)
  const [toastRoast, setToastRoast] = useState<{ text: string; tone: RoastTone } | null>(null)
  const [toastSharePayload, setToastSharePayload] = useState<TabSharePayload | null>(null)
  const roastBootstrapDone = useRef(false)

  const dismissRoastToast = useCallback(() => {
    setToastRoast(null)
    setToastSharePayload(null)
  }, [])

  useEffect(() => {
    void Promise.all([loadTabs(), loadLastRoast()]).then(([loaded, remembered]) => {
      setTabs(loaded)
      if (remembered) setLastRoast({ text: remembered.text, tone: remembered.tone })
      setReady(true)
      roastBootstrapDone.current = true
    })
  }, [])

  const persist = async (next: Tab[]) => {
    setTabs(next)
    await saveTabs(next)
  }

  const billDollars = parseDollarInput(billInput)
  const billCents = billDollars == null ? 0 : dollarsToCents(billDollars)
  const autoGratDollars = parseDollarInput(autoGratInput)
  const autoGratPercent = parsePercentInput(autoGratPercentInput)
  const autoGratCents = resolveAutoGratCents(
    billCents,
    autoGratEnabled,
    autoGratEntryMode,
    autoGratDollars,
    autoGratPercent,
  )
  const customTipDollars = parseDollarInput(customTipInput)
  const customTipPercent = parsePercentInput(customPercentInput)
  const activePreset =
    tipEntryMode === 'preset' && presetPercent != null ? presetPercent : null
  const tipCents =
    tipEntryMode === 'preset' && presetPercent != null
      ? tipCentsFromBill(billCents, presetPercent)
      : tipEntryMode === 'percent' && customTipPercent != null
        ? tipCentsFromBill(billCents, customTipPercent)
        : customTipDollars == null
          ? 0
          : dollarsToCents(customTipDollars)
  const tipPercent =
    tipEntryMode === 'preset' && presetPercent != null
      ? presetPercent
      : tipEntryMode === 'percent' && customTipPercent != null
        ? customTipPercent
        : tipPercentFromAmounts(billCents, tipCents)
  const tipInput =
    tipEntryMode === 'amount'
      ? customTipInput
      : billCents > 0
        ? centsToDollars(tipCents).toFixed(2)
        : ''
  const totalCents = tabPayTotalCents(billCents, tipCents, autoGratCents)

  const [habitRoast, setHabitRoast] = useState('Loading your damage…')

  useEffect(() => {
    if (!ready) return
    void roastForHabits(tabs).then(setHabitRoast)
  }, [tabs, ready])

  /** Top card only — no live roasts while typing; final line after Save tab. */
  const roastText = savedRoast ?? lastRoast?.text ?? habitRoast
  const roastTone: RoastTone =
    savedRoast && savedTone ? savedTone : lastRoast?.tone ?? 'habit'

  useEffect(() => {
    if (!ready || !roastBootstrapDone.current) return
    if (roastText === 'Loading your damage…') return
    void saveLastRoast(roastText, roastTone)
    setLastRoast({ text: roastText, tone: roastTone })
  }, [ready, roastText, roastTone])

  const onBillChange = (value: string) => {
    setBillInput(value)
    setSavedRoast(null)
    setSavedTone(null)
    setSource('manual')
  }

  const onTipPercent = (percent: number) => {
    setTipEntryMode('preset')
    setPresetPercent(percent)
    setCustomPercentInput('')
    setCustomTipInput('')
    setSavedRoast(null)
    setSavedTone(null)
  }

  const onCustomPercentChange = (value: string) => {
    setTipEntryMode('percent')
    setPresetPercent(null)
    setCustomPercentInput(value)
    setCustomTipInput('')
    setSavedRoast(null)
    setSavedTone(null)
  }

  const onTipChange = (value: string) => {
    setTipEntryMode('amount')
    setPresetPercent(null)
    setCustomPercentInput('')
    setCustomTipInput(value)
    setSavedRoast(null)
    setSavedTone(null)
  }

  const onPickCandidate = (cents: number) => {
    setBillInput(centsToDollars(cents).toFixed(2))
    setSavedRoast(null)
    setSavedTone(null)
  }

  const onAutoGratEnabled = (enabled: boolean) => {
    setAutoGratEnabled(enabled)
    if (!enabled) {
      setAutoGratInput('')
      setAutoGratPercentInput('')
    } else {
      setAutoGratEntryMode('percent')
      if (!autoGratPercentInput.trim()) setAutoGratPercentInput('18')
    }
    setSavedRoast(null)
    setSavedTone(null)
  }

  const onAutoGratPercentChange = (value: string) => {
    setAutoGratEntryMode('percent')
    setAutoGratPercentInput(value)
    setAutoGratInput('')
    setSavedRoast(null)
    setSavedTone(null)
  }

  const onAutoGratPercentPreset = (percent: number) => {
    setAutoGratEntryMode('percent')
    setAutoGratPercentInput(String(percent))
    setAutoGratInput('')
    setSavedRoast(null)
    setSavedTone(null)
  }

  const onAutoGratChange = (value: string) => {
    setAutoGratEntryMode('amount')
    setAutoGratInput(value)
    setSavedRoast(null)
    setSavedTone(null)
  }

  const onScan = async () => {
    setScanError(null)
    setCandidates([])
    setOpeningCamera(true)
    let capture: Awaited<ReturnType<typeof captureBillImage>>
    try {
      capture = await captureBillImage()
    } finally {
      setOpeningCamera(false)
    }
    if (!capture.ok) {
      if (capture.reason === 'permission') {
        setScanError(
          'Camera or photo access is off. Open Settings → Tab of Shame → allow Camera and Photos, or type the bill amount.',
        )
      } else if (capture.reason === 'unavailable') {
        setScanError(
          capture.detail ??
            'Bill scan is not available in this build. Update from TestFlight or enter the amount by hand.',
        )
      } else if (capture.reason === 'error') {
        setScanError(
          capture.detail
            ? `Scan could not start: ${capture.detail}`
            : 'Could not open the camera. Try again or enter the amount by hand.',
        )
      }
      return
    }
    setScanning(true)
    setPreviewUrl(capture.dataUrl)
    try {
      const result = await recognizeReceipt(capture.dataUrl)
      if (result.billCents == null) {
        setScanError('Could not find a total. Type it in — the receipt probably looks like modern art.')
        setCandidates(result.candidates)
        return
      }
      setBillInput(centsToDollars(result.billCents).toFixed(2))
      setTipEntryMode('preset')
      setPresetPercent(DEFAULT_TIP_PERCENT)
      setCustomPercentInput('')
      setCustomTipInput('')
      setCandidates(result.candidates)
      setSource('scan')
      setSavedRoast(null)
      setSavedTone(null)
    } catch (error) {
      const timedOut = error instanceof Error && /timed out/i.test(error.message)
      setScanError(
        timedOut
          ? 'Scan took too long. Try a clearer photo with the total visible, or type the amount.'
          : 'Scan failed. Try a flatter photo, or just type the number like a grown-up.',
      )
    } finally {
      setScanning(false)
    }
  }

  const onSave = async () => {
    if (billCents <= 0) return
    const tab: Tab = {
      id: newTabId(),
      createdAt: Date.now(),
      date,
      venue: venue.trim(),
      kind,
      billCents,
      tipCents,
      tipPercent,
      source,
      ...(autoGratCents > 0
        ? {
            autoGratCents,
            ...(autoGratEntryMode === 'percent' && autoGratPercent != null
              ? { autoGratPercent }
              : {}),
          }
        : {}),
    }
    await persist([tab, ...tabs])
    const payTotal = tabPayTotalCents(billCents, tipCents, autoGratCents)
    const saved = await roastForLiveBill(billCents, tipCents, tipPercent, tab.id, autoGratCents)
    setSavedRoast(saved.text)
    setSavedTone(saved.tone)
    setToastRoast({ text: saved.text, tone: saved.tone })
    setToastSharePayload(
      tabSharePayloadFromSave(tab, saved.text, saved.tone, payTotal),
    )
    setToastKey((key) => key + 1)
    setBillInput('')
    setTipEntryMode('preset')
    setPresetPercent(DEFAULT_TIP_PERCENT)
    setCustomPercentInput('')
    setCustomTipInput('')
    setVenue('')
    setCandidates([])
    setPreviewUrl(null)
    setSource('manual')
    setAutoGratEnabled(false)
    setAutoGratEntryMode('percent')
    setAutoGratPercentInput('')
    setAutoGratInput('')
    setDate(todayISODate())
  }

  const onDelete = async (id: string) => {
    await persist(tabs.filter((tab) => tab.id !== id))
  }

  /** Clears the open tab / scan UI; saved history stays on device. */
  const resetWorkingTab = useCallback(async () => {
    setBillInput('')
    setTipEntryMode('preset')
    setPresetPercent(DEFAULT_TIP_PERCENT)
    setCustomPercentInput('')
    setCustomTipInput('')
    setVenue('')
    setKind('bar')
    setDate(todayISODate())
    setScanning(false)
    setOpeningCamera(false)
    setScanError(null)
    setCandidates([])
    setPreviewUrl(null)
    setSource('manual')
    setAutoGratEnabled(false)
    setAutoGratEntryMode('percent')
    setAutoGratPercentInput('')
    setAutoGratInput('')
    setSavedRoast(null)
    setSavedTone(null)
    setToastRoast(null)
    setToastSharePayload(null)
    const habit = await roastForHabits(tabs)
    setHabitRoast(habit)
  }, [tabs])

  return (
    <div className="app-shell">
      <PullToRefresh
        enabled={screen === 'home' && ready}
        onRefresh={resetWorkingTab}
        pullingLabel="Pull to reset"
        releaseLabel="Release — clear scan & amounts"
        refreshingLabel="Clearing…"
      >
      <div className="page">
        {screen === 'privacy' ? (
          <LegalPage
            title="Privacy Policy"
            sections={PRIVACY_SECTIONS}
            onBack={() => setScreen('home')}
            onPrivacy={() => setScreen('privacy')}
            onTerms={() => setScreen('terms')}
          />
        ) : screen === 'terms' ? (
          <LegalPage
            title="Terms of Use"
            sections={TERMS_SECTIONS}
            onBack={() => setScreen('home')}
            onPrivacy={() => setScreen('privacy')}
            onTerms={() => setScreen('terms')}
          />
        ) : (
          <>
            {toastRoast ? (
              <RoastToast
                text={toastRoast.text}
                tone={toastRoast.tone}
                toastKey={toastKey}
                sharePayload={toastSharePayload}
                onDismiss={dismissRoastToast}
              />
            ) : null}
            <PromoBanner />
            <header className="flex items-end justify-between gap-3 pt-1">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-foam-faint">
                  Shame Logic
                </p>
                <h1 className="text-3xl font-bold tracking-tight">{APP_NAME}</h1>
                <p className="mt-1 text-sm text-foam-muted">{APP_TAGLINE}</p>
              </div>
            </header>

            <RoastCard
              text={ready ? roastText : 'Loading your damage…'}
              tone={roastTone}
              showNavigatorCta={roastTone === 'navigator'}
            />

            <TipCalculator
              billInput={billInput}
              tipInput={tipInput}
              tipPercent={tipPercent}
              presetPercent={activePreset}
              tipEntryMode={tipEntryMode}
              customPercentInput={customPercentInput}
              venue={venue}
              kind={kind}
              date={date}
              scanning={scanning}
              openingCamera={openingCamera}
              scanError={scanError}
              candidates={candidates}
              previewUrl={previewUrl}
              billCents={billCents}
              tipCents={tipCents}
              autoGratCents={autoGratCents}
              autoGratEnabled={autoGratEnabled}
              autoGratEntryMode={autoGratEntryMode}
              autoGratPercentInput={autoGratPercentInput}
              autoGratInput={autoGratInput}
              totalCents={totalCents}
              onBillChange={onBillChange}
              onAutoGratEnabled={onAutoGratEnabled}
              onAutoGratPercentChange={onAutoGratPercentChange}
              onAutoGratPercentPreset={onAutoGratPercentPreset}
              onAutoGratChange={onAutoGratChange}
              onTipPercent={onTipPercent}
              onCustomPercentChange={onCustomPercentChange}
              onTipChange={onTipChange}
              onVenue={setVenue}
              onKind={setKind}
              onDate={setDate}
              onScan={() => void onScan()}
              onPickCandidate={onPickCandidate}
              onSave={() => void onSave()}
            />

            <TabHistory tabs={tabs} onDelete={(id) => void onDelete(id)} />
            <LegalFooter onPrivacy={() => setScreen('privacy')} onTerms={() => setScreen('terms')} />
          </>
        )}
      </div>
      </PullToRefresh>
    </div>
  )
}
